// @flow

import React, { memo, useEffect, useCallback } from 'react'
import { Trans } from 'react-i18next'
import { or, not } from 'ramda'
import { isNotNilOrEmpty, isFalse } from 'ramda-adjunct'

import { useFocusable, useMouseOver } from '@alphaott/smart-tv-spatial-navigation'

import {
  Container,
  Top,
  Title,
  Description,
  Emails,
  EmailItem,
  Label,
  Text,
  Phones,
  PhoneItems,
  PhoneItem,
  Bottom,
  Address,
} from './CustomerSupport.styles'

export type CustomerSupportProps = {
  focusKey: string,
  className?: string,
  supportEmail: string,
  infoEmail: string,
  companyName: string,
  supportAddress: string,
  companyWww: string,
  companyPhones?: string[],
  companyWA?: string[],
  focusable?: boolean,
  withMouseOver?: boolean,
  onScroll?: Function,
  onEnterPress?: Function,
}

// eslint-disable-next-line complexity
const CustomerSupportPure = ({
  focusKey: parentFocusKey,
  className,
  companyName,
  companyWww,
  supportAddress,
  infoEmail,
  supportEmail,
  companyPhones,
  companyWA,
  focusable = true,
  withMouseOver = true,
  onScroll,
  onEnterPress,
}: CustomerSupportProps) => {
  const { ref, focused, focusSelf } = useFocusable({
    focusKey: parentFocusKey,
    focusable,
    onEnterPress,
  })
  const { handleMouseOver: onMouseOver } = useMouseOver(focusSelf)

  const renderPhones = useCallback(
    (phones) =>
      phones.map((phone) => (
        <PhoneItem key={phone}>
          <Text>{phone}</Text>
        </PhoneItem>
      )),
    [],
  )

  const handleMouseOver = useCallback(
    // eslint-disable-next-line consistent-return
    (event) => {
      if (or(not(focusable), isFalse(withMouseOver))) return null
      onMouseOver(event)
    },
    [focusable, withMouseOver, onMouseOver],
  )

  useEffect(() => {
    if (focused && onScroll) {
      onScroll()
    }
  }, [focused]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container ref={ref} className={className} focused={focused} onMouseOver={handleMouseOver}>
      <Top>
        <Title>
          <Trans i18nKey="mwstv_support_support_title" defaults="24/7 Customer Support" />
        </Title>

        <Description>
          <Trans
            i18nKey="mwstv_support_support_description"
            defaults="For Customer Support,
                  refer to the contacts below according to your requests."
          />
        </Description>

        {(supportEmail || infoEmail) && (
          <Emails>
            {supportEmail && (
              <EmailItem>
                <Label>
                  <Trans i18nKey="support" defaults="Support" />
                </Label>
                <Text>{supportEmail}</Text>
              </EmailItem>
            )}

            {infoEmail && (
              <EmailItem>
                <Label>
                  <Trans i18nKey="mwstv_support_general_inquiries" defaults="General Inquiries" />
                </Label>
                <Text>{infoEmail}</Text>
              </EmailItem>
            )}
          </Emails>
        )}

        {isNotNilOrEmpty(companyPhones) && (
          <Phones>
            <Label>
              <Trans i18nKey="mwstv_support_phones" defaults="Phones" />:
            </Label>
            <PhoneItems>{renderPhones(companyPhones)}</PhoneItems>
          </Phones>
        )}

        {isNotNilOrEmpty(companyWA) && (
          <Phones>
            <Label>
              <Trans i18nKey="mwstv_support_whatsapp" defaults="WhatsApp" />:
            </Label>
            <PhoneItems>{renderPhones(companyWA)}</PhoneItems>
          </Phones>
        )}
      </Top>

      <Bottom>
        <Address>
          {companyName}
          <br />
          {supportAddress}
        </Address>
        <Text>{companyWww}</Text>
      </Bottom>
    </Container>
  )
}

export const CustomerSupport = memo<CustomerSupportProps>(CustomerSupportPure)

export default CustomerSupport
